import {JSONNaN} from '@wandb/weave/core';
import React from 'react';
import {useHistory} from 'react-router-dom';

import {useAutoRefreshSettings} from '../containers/AutoRefreshSettings';
import {useCodeFilesQuery, useRunCodePollingQuery} from '../generated/graphql';
import {useBasePollInterval} from '../pages/Run/useBasePollInterval';
import ArtifactFiles from './ArtifactFiles';
import NoMatch from './NoMatch';
import {TabWaveLoader} from './RunDetails/TabWaveLoader';
import RunFiles from './RunFiles/RunFiles';

type CodeFilesProps = {
  entityName: string;
  projectName: string;
  runName: string;
  filePath: string;
};

export const CodeFiles = ({
  entityName,
  projectName,
  runName,
  filePath,
}: CodeFilesProps) => {
  const history = useHistory();
  const {autoRefreshEnabled} = useAutoRefreshSettings();
  const {data: runCodePollingQuery, loading: runCodePollingLoading} =
    useRunCodePollingQuery({
      variables: {
        entityName,
        projectName,
        runName,
      },
    });
  const pollInterval = useBasePollInterval({
    runState: runCodePollingQuery?.project?.run?.state ?? undefined,
    runHistoryKeys: runCodePollingQuery?.project?.run?.historyKeys,
    autoRefreshEnabled,
  });
  const pollIntervalMaybeDisabled = autoRefreshEnabled ? pollInterval : 0;
  const {data, loading} = useCodeFilesQuery({
    variables: {
      projectName,
      entityName,
      runName,
    },
    pollInterval: pollIntervalMaybeDisabled,
  });

  const run = data?.project?.run;
  const config = JSONNaN.JSONparseNaN(run?.config);
  // 'wandb_version' is an extra thing put in by the CLI that is a misnomer
  // and useless.
  delete config?.wandb_version;

  if (loading || runCodePollingLoading) {
    return <TabWaveLoader />;
  }

  if (!run) {
    return <NoMatch />;
  }

  if (run.codeArtifact) {
    return (
      <ArtifactFiles
        entityName={entityName}
        projectName={projectName}
        filePath={filePath}
        artifactSequenceName={run.codeArtifact.artifactSequence.name}
        artifactCommitHash={run.codeArtifact.commitHash ?? ''}
        runName={runName}
      />
    );
  }

  return (
    <RunFiles
      entityName={entityName}
      projectName={projectName}
      runName={runName}
      path={
        config._wandb.value.code_path || config._wandb.value.session_history
      }
      history={history}
    />
  );
};
